import {
  integer, maxLength, minLength, requiredIf,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { StringMap, TypeCampaignEnum } from '@/types';
import { atLeastOneSegmentValidator, includesValuesValidator, requiredWithMessage } from '@/helpers/CustomValidator';
import type {
  FlowOperatorMetadata,
  ValidationResult,
  NumberRange,
  Period,
  PickerData,
} from '@/types/flow-types';
import Form from './Form.vue';

export interface PurchaseHistoryFilters {
  filterhasordered?: {
    segment_number: number;
    hidden_has_ordered: string[];
    check_if_order_valid: string[];
    litteral_title: StringMap;
  };
  filternoorderfrom?: {
    segment_number: number;
    time_unit_select: Period[];
    litteral_title: StringMap;
  };
  filtertimesincelastorder?: {
    segment_number: number;
    time_unit_select: Period[];
    check_if_order_valid: string[];
    litteral_title: StringMap;
  };
  filterdateordercount?: {
    segment_number: number;
    range_slider_date_order_count_time: NumberRange;
    time_unit_select: Period[];
    check_if_order_valid: string[];
    litteral_title: StringMap;
  };
  filterdateorderamountcurrency?: {
    segment_number: number;
    date_order_amount_currency: NumberRange;
    time_unit_select: Period[];
    check_if_order_valid: string[];
    litteral_title: StringMap;
  };
  filterdateorderproductcount?: {
    segment_number: number;
    product_picker_date_order_product_count: PickerData;
    time_unit_select: Period[];
    check_if_order_valid: string[];
    litteral_title: StringMap;
  };
  filterdateordercategorycount?: {
    segment_number: number;
    product_picker_date_order_category_count: PickerData;
    time_unit_select: Period[];
    check_if_order_valid: string[];
    litteral_title: StringMap;
  };
  filterdateordermanufacturercounttime?: {
    segment_number: number;
    product_picker_date_order_manufacturer_count: PickerData;
    time_unit_select: Period[];
    check_if_order_valid: string[];
    litteral_title: StringMap;
  };
}

export interface PurchaseHistoryData {
  custom_title: string;
  custom_description: string;
  outputs_number: number;
  initial_number_outputs?: null | number;
  grant_additional_delay: string;
  extra_time: Period;
  inclusion: PurchaseHistoryFilters;
  exclusion: PurchaseHistoryFilters;
}

export function createDefaultData(data: Partial<PurchaseHistoryData> = {}): PurchaseHistoryData {
  return {
    custom_title: data.custom_title ?? '',
    custom_description: data.custom_description ?? '',
    outputs_number: data.outputs_number ?? 1,
    initial_number_outputs: data.initial_number_outputs,
    grant_additional_delay: data.grant_additional_delay ?? '0',
    extra_time: data.extra_time ?? { value: 1, unit: 'DAY' },
    inclusion: data.inclusion ?? {},
    exclusion: data.exclusion ?? {},
  };
}

const PurchaseHistory: FlowOperatorMetadata<PurchaseHistoryData> = {
  id: 'boxfiltre_4',
  kind: 'filter',
  label: 'automatedScenarios.filters.purchaseHistory',
  icon: 'fa-cogs',
  hasConfiguration: true,
  // Deprecated: replaced by ContactSegmentation ("Conditions logiques").
  // Metadata kept registered so existing flows still render and validate.
  availableIn: [],

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: ['boxabandonpanier', 'boxperiodiclaunching'],
    },
    [TypeCampaignEnum.BULK]: {
      nextOperators: ['boxchoosebulklist', 'boxaudiencesetup'],
    },
  },

  persistent: [
    { segmentId: 'filterhasordered', boxId: '' },
    { segmentId: 'filternoorderfrom', boxId: '' },
    { segmentId: 'filtertimesincelastorder', boxId: '' },
    { segmentId: 'filterdateordercount', boxId: '' },
    { segmentId: 'filterdateorderamountcurrency', boxId: '' },
    { segmentId: 'filterdateorderproductcount', boxId: '' },
    { segmentId: 'filterdateordercategorycount', boxId: '' },
    { segmentId: 'filterdateordermanufacturercounttime', boxId: '' },
  ],

  Form,

  defaultData: createDefaultData,

  outputs: (data: PurchaseHistoryData) => data.outputs_number,

  getDisplayLabel(data: PurchaseHistoryData): string | null {
    return data.custom_title || null;
  },

  getSubtitle(data: PurchaseHistoryData): string | null {
    return data.custom_description || null;
  },

  async validate(data: PurchaseHistoryData): Promise<ValidationResult> {
    const normalizedData = {
      ...data,
      outputs_number: Number(data.outputs_number),
    };

    const rules = {
      custom_title: {
        requiredWithMessage: requiredWithMessage(),
        minLength: minLength(3),
        maxLength: maxLength(45),
      },
      outputs_number: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator([1, 2]),
      },
      grant_additional_delay: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator(['0', '1']),
      },
      extra_time: {
        value: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          integer,
        },
        unit: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          includesValuesValidator: includesValuesValidator(['SECOND', 'MINUTE', 'HOUR', 'DAY']),
        },
      },
      inclusion: {
        required: atLeastOneSegmentValidator(data.exclusion),
      },
    };

    const v$ = useVuelidate(rules, normalizedData);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$,
    };
  },

  beforeInsert(data: PurchaseHistoryData): PurchaseHistoryData {
    const dataToReturn = { ...data };
    delete dataToReturn.initial_number_outputs;
    return dataToReturn;
  },

  canBeDeleted(): boolean {
    return true;
  },
};

export default PurchaseHistory;
